import React, { useState } from 'react';
import { FiCalendar, FiEye } from 'react-icons/fi';
import './RosterGrid.css';
import { useRoster } from './hooks/useRoster';
import { StaffCell } from './StaffCell';
import CalendarView from './CalendarView';
import { FaUserPlus } from 'react-icons/fa';
const RosterGrid = () => {
  const {
    shifts,
    dateRange,
    rosterEntries,
    staff,
  } = useRoster();

  const [viewType, setViewType] = useState('day-shift-staff');

  const getStaffForShift = (date, shiftId) => {
    return rosterEntries
      .filter(entry => entry.date === date && entry.shiftId === shiftId)
      .map(entry => staff.find(s => s.id === entry.staffId))
      .filter(Boolean);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return {
      day: date.getDate(),
      dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
      month: date.toLocaleDateString('en-US', { month: 'short' }),
    };
  };

  const getShiftTypeClass = (shiftType) => {
    switch (shiftType) {
      case 'morning': return 'shift-morning';
      case 'afternoon': return 'shift-afternoon';
      case 'night': return 'shift-night';
      default: return 'shift-off';
    }
  };

  return (
    <div className="roster-container">
      {/* Roster Controls */}
      <div className="roster-controls-card">
        <div className="controls-header">
          <div className="title-section">
            <FiCalendar className="icon" />
            <h2>Roster View</h2>
          </div>
          <div className="controls">
            <select
              value={viewType}
              onChange={(e) => setViewType(e.target.value)}
              className="view-select"
            >
              <option value="day-shift-staff">Day → Shift → Staff</option>
              <option value="shift-date-staff">Shift → Date → Staff</option>
              <option value="calendar">Calendar View</option>
            </select>
            <button className="view-options-btn">
              <FiEye className="icon-small" />
              View Options
            </button>
          </div>
        </div>
        <div className="info-bar">
          <span>Period: {formatDate(dateRange[0]).day} {formatDate(dateRange[0]).month} - {formatDate(dateRange[dateRange.length - 1]).day} {formatDate(dateRange[dateRange.length - 1]).month}</span>
          <span className="info-badge">{dateRange.length} days</span>
          <span className="info-badge">{shifts.length} shifts/day</span>
        </div>
      </div>

      {/* Roster View */}
      {viewType === 'calendar' ? (
        <CalendarView />
      ) : viewType === 'day-shift-staff' ? (
        <div className="day-shift-view">
          {dateRange.slice(0, 2).map(date => {
            const dateInfo = formatDate(date);
            return (
              <div key={date} className="date-card">
                <div className="date-head">
                  <div className="date-display">
                    <div className="day-number">{dateInfo.day}</div>
                    <div className="date-text">
                      <div className="day-name">{dateInfo.dayName}</div>
                      <div className="month-name">{dateInfo.month}</div>
                    </div>
                  </div>
                </div>
                <div className="shifts-container">
                  {shifts.map(shift => {
                    const assignedStaff = getStaffForShift(date, shift.id);
                    return (
                      <div key={shift.id} className={`shift-card ${getShiftTypeClass(shift.type)}`}>
                        <div className="shift-header">
                          <div className="shift-info">
                            <h4>{shift.name}</h4>
                            <p>{shift.startTime} - {shift.endTime}</p>
                          </div>
                          <span className={`staff-count ${assignedStaff.length >= shift.requiredStaff ? 'complete' : 'incomplete'}`}>
                            {assignedStaff.length}/{shift.requiredStaff}
                          </span>
                        </div>
                        <div className="staff-slots">
                          {assignedStaff.map(staff => staff && (
                            <StaffCell
                              key={staff.id}
                              staff={staff}
                              date={date}
                              shiftId={shift.id}
                              showActions
                            />
                          ))}
                          {Array.from({ length: Math.max(0, shift.requiredStaff - assignedStaff.length) }).map((_, i) => (
                            <div key={`empty-${i}`} className="empty-slot">
                              <FaUserPlus className="btn-icon" />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="shift-date-view">
          {shifts.map(shift => (
            <div key={shift.id} className="shift-section">
              <div className={`shift-section-header ${getShiftTypeClass(shift.type)}`}>
                <div className="shift-section-title">
                  <div className="shift-name">{shift.name}</div>
                  <div className="shift-time">{shift.startTime} - {shift.endTime}</div>
                </div>
                <span className="required-badge">Required: {shift.requiredStaff} staff</span>
              </div>

              <div className="date-grid">
                {dateRange.map(date => {
                  const dateInfo = formatDate(date);
                  const assignedStaff = getStaffForShift(date, shift.id);
                  const remaining = Math.max(0, shift.requiredStaff - assignedStaff.length);
                  return (
                    <div key={date} className="date-tile">
                      <div className="tile-day">{dateInfo.dayName}</div>
                      <div className="tile-num">{dateInfo.day}</div>
                      <div className="tile-foot">{remaining > 0 ? `Need ${remaining} more` : 'Complete'}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RosterGrid;
